import { useEffect, useRef, useState, useCallback } from "react";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

// ── Types ─────────────────────────────────────────────────────────────────────

/* eslint-disable unused-imports/no-unused-vars */
export interface SseMessage<T = unknown> {
  /** Event type (e.g. "status_changed", "deployment", "notification.new") */
  type: string;
  /** Event payload */
  data: T;
}

export interface UseSseResult<T = unknown> {
  /** Whether the EventSource is currently connected */
  connected: boolean;
  /** The last error from the EventSource (null when healthy) */
  error: string | null;
  /** Manually close and reopen the connection */
  reconnect: () => void;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useSse — generic Server-Sent Events hook using the native EventSource API.
 *
 * Connects to `path` (relative to apiBaseUrls.BACKEND) and invokes `onMessage`
 * for each incoming event. The JWT auth token is appended as `?token=` query
 * param because EventSource does not support custom headers.
 *
 * Features:
 *   - Auto-reconnects (EventSource does this natively; we track state)
 *   - Cleans up on unmount
 *   - Stable callback via ref (no effect re-run on parent re-render)
 *   - Exposes `connected` and `error` state
 *
 * @param path       SSE endpoint path (e.g. "sse/stack/my-stack")
 * @param onMessage  Callback invoked for each SSE message
 */
export function useSse<T = unknown>(
  path: string | null,
  onMessage: (msg: SseMessage<T>) => void,
): UseSseResult<T> {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const callbackRef = useRef(onMessage);
  callbackRef.current = onMessage;

  const connect = useCallback(() => {
    if (!path) return;

    // Close existing connection
    eventSourceRef.current?.close();

    const token = orgSessionManager.getToken() ?? "";
    const base = apiBaseUrls.BACKEND;
    const url = `${base}/api/v1/${path}?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      setConnected(true);
      setError(null);
    };

    es.onerror = () => {
      setConnected(false);
      setError("Connection lost — reconnecting…");
      // EventSource auto-reconnects; we just track state
    };

    // Listen for named events — NestJS SSE sends events with `type` field
    // in the data payload. We use a generic "message" listener and parse.
    es.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as SseMessage<T>;
        if (parsed.type === "error") {
          setError(
            String(
              (parsed.data as Record<string, string>)?.message ?? "SSE error",
            ),
          );
          es.close();
          setConnected(false);
          return;
        }
        if (parsed.type === "connected") {
          setConnected(true);
          setError(null);
          return;
        }
        if (parsed.type === "heartbeat") {
          return; // Keep-alive, ignore
        }
        callbackRef.current(parsed);
      } catch {
        // Non-JSON data, skip
      }
    };
  }, [path]);

  useEffect(() => {
    connect();
    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connect]);

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  return { connected, error, reconnect };
}
